import { Box, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { PermissionCheckboxGrid } from "components/PermissionCheckboxGrid";
import { Permission, Role } from "generated-graphql/graphql";
import { useState } from "react";

export type RoleEditorSaveInput = {
  name: string;
  permissions: Permission[];
};

type RoleEditorDialogProps = {
  /** The role being edited, or null when creating a new role. */
  role: Role | null;
  tenantName: string;
  saving: boolean;
  onClose: () => void;
  onSave: (input: RoleEditorSaveInput) => void;
};

/**
 * Form for creating or editing a role's permissions. This component is mounted
 * fresh each time the editor opens (rendered conditionally by the parent), so
 * the form state initializes directly from the `role` prop with no effect.
 */
export function RoleEditorDialog({
  role,
  tenantName,
  saving,
  onClose,
  onSave,
}: RoleEditorDialogProps) {
  const [name, setName] = useState(role?.name ?? "");
  const [permissions, setPermissions] = useState<Permission[]>(role?.permissions ?? []);

  const trimmedName = name.trim();
  const isCreate = !role;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={isCreate ? "Create role" : `Edit role`}
      description={`Choose which permissions this role grants in ${tenantName}. Changes apply to every user assigned this role.`}
      primaryActionButtonProps={{
        loading: saving,
        disabled: !trimmedName || permissions.length === 0,
        onClick: () => onSave({ name: trimmedName, permissions }),
        saveText: isCreate ? "Create" : "Save",
      }}
    >
      <Box sx={{ mt: 1, mb: 2 }}>
        <TextField
          label="Role name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Permissions ({permissions.length})
      </Typography>
      <PermissionCheckboxGrid selectedPermissions={permissions} onChange={setPermissions} />
    </Dialog>
  );
}
